import CategoryOptionLabel from '@app/src/components/CategoryOptionLabel'
import { usePeriodName } from '@app/src/hooks/usePeriodName'
import type { FacetItem } from '@app/src/pages/ResourceCollection'
import type { FilterValue } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { addSpaceBetweenWords } from '@app/src/utils/helpers'
import React from 'react'
import { useIntl } from 'react-intl'
import useLocalisedCountryName from './useLocalisedCountryName'

export const useGetFilterLabels = (complexValues: { [key: string]: FacetItem[] }) => {
  const { formatMessage } = useIntl()
  const { formatPeriodName } = usePeriodName()
  const { getLocalisedCountryName } = useLocalisedCountryName()
  const getFilterLabel = (property: string, value: FilterValue | undefined, operator?: Operators): React.ReactNode => {
    if (Array.isArray(value)) {
      if (property.endsWith('priority')) {
        return `${formatMessage({ id: `dashboard.sourcing.companyStatistics.priority` })}: ${value
          .map(v =>
            formatMessage({
              id: `schemas.provider.priorityValues.${v}`,
            }),
          )
          .join(', ')}`
      }
      if (property.endsWith('actorType.id') && complexValues?.actorTypeNames) {
        return `${formatMessage({
          id: `dashboard.sourcing.companyStatistics.actorType`,
        })}: ${complexValues.actorTypeNames
          ?.map(actorType => formatMessage({ id: `schemas.mappingNode.actorTypeValues.${actorType.label}` }))
          .join(', ')}`
      }
      if (property.endsWith('actorType.name')) {
        return `${formatMessage({ id: `dashboard.sourcing.companyStatistics.actorType` })}: ${value
          .map(v => addSpaceBetweenWords(v))
          .join(', ')}`
      }
      if (property.endsWith('mappingNodes.tier')) {
        return `${formatMessage({ id: `dashboard.sourcing.companyStatistics.productMappingTier` })}: ${value
          .map(v => formatMessage({ id: 'schemas.provider.tierValues' }, { tier: v }))
          .join(', ')}`
      }
      if (property.endsWith('product.productStatus')) {
        return `${formatMessage({ id: 'schemas.product.productStatus' })}: ${value
          .map(v => formatMessage({ id: `schemas.product.productStatusValues.${v}` }))
          .join(', ')}`
      }
      if (property.endsWith('tier')) {
        return `${formatMessage({ id: `dashboard.sourcing.companyStatistics.tier` })}: ${value
          .map(v => formatMessage({ id: 'schemas.provider.tierValues' }, { tier: v }))
          .join(', ')}`
      }
      if (property.endsWith('supplierUsage')) {
        return `${formatMessage({ id: `dashboard.sourcing.companyStatistics.usage` })}: ${value
          .map(v =>
            formatMessage({
              id: `schemas.provider.supplierUsageValues.${v}`,
            }),
          )
          .join(', ')}`
      }
      if (property.endsWith('finalRiskRating')) {
        return `${formatMessage({ id: `dashboard.sourcing.companyStatistics.finalRiskRating` })}: ${value
          .map(v =>
            formatMessage({
              id: `schemas.provider.finalRiskRatingValues.${v}`,
            }),
          )
          .join(', ')}`
      }
      if (property.endsWith('providerApprovalStatus')) {
        return `${formatMessage({ id: `dashboard.sourcing.companyStatistics.approvalStatus` })}: ${value
          .map(v =>
            formatMessage({
              id: `schemas.provider.providerApprovalStatusValues.${v}`,
            }),
          )
          .join(', ')}`
      }
      if (property.endsWith('activityStatus')) {
        return `${formatMessage({ id: `dashboard.sourcing.companyStatistics.activityStatus` })}: ${value
          .map(v => addSpaceBetweenWords(v))
          .join(', ')}`
      }
      if (property.endsWith('country.id') && complexValues?.countryNames) {
        return `${formatMessage({ id: `dashboard.sourcing.companyStatistics.country` })}: ${complexValues.countryNames
          ?.map(countryName => {
            const localisedCountryName = getLocalisedCountryName(countryName.label)
            return localisedCountryName || countryName.label
          })
          .join(', ')}`
      }
      if (property.endsWith('target.id') && complexValues?.companyNames) {
        return `${formatMessage({ id: `dashboard.sourcing.companyStatistics.companies` })}: ${complexValues.companyNames
          ?.map(companyName => companyName.value)
          .join(', ')}`
      }
      if (property.endsWith('target.organizationId') && complexValues?.organizationNames) {
        return `${formatMessage({ id: `dashboard.sourcing.companyStatistics.companies` })}: ${complexValues.organizationNames
          ?.map(companyName => companyName.label)
          .join(', ')}`
      }
      if (property.endsWith('providerOwners.userId') && complexValues?.userNames) {
        return `${formatMessage({ id: `dashboard.sourcing.companyStatistics.owner` })}: ${complexValues.userNames
          ?.map(userName => userName.label)
          .join(', ')}`
      }
      if (property.endsWith('questionId') && complexValues?.questionNames) {
        return `${formatMessage({ id: `schemas.filter.questions` })}: ${complexValues.questionNames
          ?.map(question => question.label)
          .join(', ')}`
      }
      if (property.endsWith('questionnaireTemplateId') && complexValues?.questionnaireNames) {
        return `${formatMessage({ id: `resourceTypes.questionnaires` })}: ${complexValues.questionnaireNames
          ?.map(questionnaire => questionnaire.label)
          .join(', ')}`
      }
      if (property.endsWith('template.options.id') && complexValues?.legislationNames) {
        return `${formatMessage({ id: `schemas.question.legislation` })}: ${complexValues.legislationNames
          ?.map(legislation => legislation.label)
          .join(', ')}`
      }
      if (property.endsWith('spends.periodName')) {
        if (operator === Operators.NotEqualTo && typeof value === 'string') {
          return formatMessage({ id: 'schemas.filter.spendPeriodMissing' }, { period: formatPeriodName(value) })
        }
        if (complexValues?.periodNames) {
          return `${formatMessage({ id: `dataHub.spendPeriods` })}: ${complexValues.periodNames
            ?.map(period => formatPeriodName(period.label))
            .join(', ')}`
        }
      }
      if (property.endsWith('spendClassification')) {
        return `${formatMessage({ id: 'schemas.filter.spendInterval' })}: ${value.map(v => formatMessage({ id: 'statistics.dataInsights.spend.intervalLabel' }, { spendClassification: v })).join(', ')}`
      }
      if (property.endsWith('periodName') && complexValues?.periodNames) {
        return `${formatMessage({ id: `dataHub.periods` })}: ${complexValues.periodNames
          ?.map(period => formatPeriodName(period.label))
          .join(', ')}`
      }
      if (property.endsWith('industry.id') && complexValues?.industries) {
        return `${formatMessage({ id: 'claim.createClaim.orgInfoStep.industry' })}: ${complexValues.industries
          ?.map(industry => industry.label)
          .join(', ')}`
      }
      if (property.endsWith('verifications.externalVerificationStatus') && complexValues?.verificationStatuses) {
        return `${formatMessage({ id: 'filters.responseItem.externalVerificationStatus' })}: ${complexValues.verificationStatuses
          ?.map(status =>
            formatMessage({
              id: `reporting.reviewStatuses.${status.label}`,
            }),
          )
          .join(', ')}`
      }
      if (
        property.endsWith('verifications.internalVerificationStatus') &&
        complexValues?.internalVerificationStatuses
      ) {
        return `${formatMessage({ id: 'filters.responseItem.internalVerificationStatus' })}: ${complexValues.internalVerificationStatuses
          ?.map(status =>
            formatMessage({
              id: `reporting.internalVerificationStatuses.${status.label}`,
            }),
          )
          .join(', ')}`
      }

      if (property.endsWith('categoryOptions.id')) {
        const categoryLabel =
          complexValues?.categoryNames?.[0]?.label ||
          formatMessage({ id: `dashboard.sourcing.companyStatistics.customCategories` })
        return (
          <>
            {`${categoryLabel}: `}

            {value.map((v, i) => (
              <CategoryOptionLabel
                key={v}
                categoryOptionId={String(v)}
                categoryCount={value.length}
                isLastItem={i === value.length - 1}
              />
            ))}
          </>
        )
      }
      return ''
    }
    if (property.endsWith('spends.periodName') && typeof value === 'string') {
      if (operator === Operators.NotEqualTo) {
        return formatMessage({ id: 'schemas.filter.spendPeriodMissing' }, { period: formatPeriodName(value) })
      }
      return `${formatMessage({ id: 'schemas.filter.spendPeriod' })}: ${formatPeriodName(value)}`
    }
    if (property.endsWith('periodName') && complexValues?.periodNames) {
      return `${formatMessage({ id: `dataHub.periods` })}: ${complexValues.periodNames?.map(period =>
        formatPeriodName(period.label),
      )}`
    }
    if (property.endsWith('submittedAt')) {
      return `${formatMessage({ id: `schemas.response.${operator === Operators.GreaterThanOrEqual ? 'responseDateFrom' : 'responseDateTo'}` })}: ${value}`
    }

    return ''
  }

  return { getFilterLabel }
}
